import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService, { PerformanceHistory } from '../services/api';
import DailyPerformanceTableBloomberg from './DailyPerformanceTableBloomberg';
import TWRPerformanceBloomberg from './TWRPerformanceBloomberg';
import { UnifiedChartsBloomberg } from './bloomberg';
import '../styles/bloomberg-theme.css';

interface User {
  id: string;
  email: string;
  wallet_address: string;
  wallet_label: string;
  current_value: number;
  subscription_status: string;
  created_at: string;
}

interface AdminDashboardProps {
  preSelectedUserId?: string;
}

const AdminDashboardBloomberg: React.FC<AdminDashboardProps> = ({ preSelectedUserId }) => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(preSelectedUserId || null);
  const [performanceData, setPerformanceData] = useState<PerformanceHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (preSelectedUserId) {
      setSelectedUserId(preSelectedUserId);
    }
  }, [preSelectedUserId]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await apiService.getAdminUsers();
        setUsers(data);
        if (data.length > 0 && !selectedUserId && !preSelectedUserId) {
          setSelectedUserId(data[0].id);
        }
      } catch (err) {
        setError('FAILED TO LOAD USERS');
        console.error(err);
      }
    };

    if (user?.is_admin) {
      fetchUsers();
    }
  }, [user, selectedUserId, preSelectedUserId]);

  const fetchPerformanceData = useCallback(async () => {
    if (!selectedUserId) return;

    try {
      setLoading(true);
      const data = await apiService.getAdminUserPerformance(selectedUserId, '7d');
      setPerformanceData(data);
      setError(null);
    } catch (err) {
      setError('FAILED TO LOAD PERFORMANCE DATA');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedUserId]);

  useEffect(() => {
    if (selectedUserId) {
      fetchPerformanceData();
      const interval = setInterval(fetchPerformanceData, 60000);
      return () => clearInterval(interval);
    }
  }, [fetchPerformanceData, selectedUserId]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  if (!user?.is_admin) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <span style={{ color: 'var(--bb-red)' }}>ADMIN ACCESS REQUIRED</span>
      </div>
    );
  }

  if (loading && !performanceData) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <span className="bb-blink" style={{ color: 'var(--bb-amber)' }}>LOADING...</span>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* User Selector */}
      <div className="bb-panel" style={{ marginBottom: '20px' }}>
        <div className="bb-panel-header">SELECT USER</div>
        <div className="bb-panel-content" style={{ padding: '15px' }}>
          <select
            value={selectedUserId || ''}
            onChange={(e) => setSelectedUserId(e.target.value)}
            style={{
              width: '100%',
              maxWidth: '600px',
              padding: '8px',
              backgroundColor: 'var(--bb-black)',
              color: 'var(--bb-green)',
              border: '1px solid var(--bb-amber)',
              fontFamily: 'var(--bb-font-mono)',
              fontSize: '12px'
            }}
          >
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.email} - {u.wallet_address?.slice(0, 8)}... ({formatCurrency(u.current_value)})
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '10px',
          border: '1px solid var(--bb-red)',
          backgroundColor: 'rgba(255, 0, 0, 0.1)',
          marginBottom: '20px'
        }}>
          <span style={{ color: 'var(--bb-red)' }}>ERROR: {error}</span>
        </div>
      )}

      {performanceData && (
        <>
          {/* TWR Performance */}
          <div style={{ marginBottom: '20px' }}>
            <TWRPerformanceBloomberg isAdmin={true} userId={selectedUserId || undefined} />
          </div>

          {/* Unified Charts */}
          <UnifiedChartsBloomberg
            userId={selectedUserId || undefined}
            initialPeriod="all"
          />

          {/* Daily Performance */}
          <DailyPerformanceTableBloomberg userId={selectedUserId || undefined} />
        </>
      )}
    </div>
  );
};

export default AdminDashboardBloomberg;